import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { soccerPath, runningPath } from '../utils/paths';
import { isImageFile } from '../utils/imageUpload';
import AppSwitcher from './AppSwitcher';
import { SITE_NAME } from '../config/siteBranding';
import './PlayerRegistration.css';
import './AccountProfile.css';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8080/api').replace(/\/api\/?$/, '') || 'http://localhost:8080';

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const profileImgUrl = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : `${API_BASE}${url}`;
};

const AccountProfile = () => {
  const { user, isAuthenticated, updateUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    dateOfBirth: '',
  });
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [profilePreviewUrl, setProfilePreviewUrl] = useState(null);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await api.getAccountProfile(user.id);
        setProfile(data);
        setFormData({
          name: data.name || '',
          phoneNumber: data.phoneNumber || '',
          dateOfBirth: data.dateOfBirth ? data.dateOfBirth.slice(0, 10) : '',
        });
      } catch (err) {
        setError(err.message || 'Could not load account profile');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isAuthenticated, user?.id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setProfilePictureFile(null);
      if (profilePreviewUrl) URL.revokeObjectURL(profilePreviewUrl);
      setProfilePreviewUrl(null);
      return;
    }
    if (!isImageFile(file)) {
      setError('Please select an image file.');
      return;
    }
    setProfilePictureFile(file);
    setProfilePreviewUrl(URL.createObjectURL(file));
    setError('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      let profilePictureUrl = profile?.profilePictureUrl || null;
      if (profilePictureFile) {
        profilePictureUrl = await api.uploadAccountProfilePicture(profilePictureFile);
      }
      const updated = await api.updateAccountProfile(user.id, {
        name: formData.name.trim(),
        phoneNumber: formData.phoneNumber || null,
        dateOfBirth: formData.dateOfBirth || null,
        ...(profilePictureUrl && { profilePictureUrl }),
      });
      setProfile(updated);
      updateUser({ name: updated.name });
      setEditing(false);
      setProfilePictureFile(null);
      if (profilePreviewUrl) URL.revokeObjectURL(profilePreviewUrl);
      setProfilePreviewUrl(null);
      setSuccess('Account profile updated. Changes apply across soccer and running.');
    } catch (err) {
      setError(err.message || 'Failed to update account');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.changePassword({
        userId: user.id,
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setSuccess('Password updated.');
    } catch (err) {
      setError(err.message || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="account-profile-page">
        <p><Link to="/login">Log in</Link> to manage your account.</p>
      </div>
    );
  }

  const photoSrc = profilePreviewUrl || profileImgUrl(profile?.profilePictureUrl);

  return (
    <div className="account-profile-page">
      <header className="account-profile-page__header">
        <Link to="/" className="account-profile-page__home">
          {SITE_NAME}
        </Link>
        <AppSwitcher variant="compact" />
      </header>

      <div className="card account-profile-page__card">
        <h1>Account profile</h1>
        <p className="form-help">
          Name, photo, and contact details are shared across all sports. Sport-specific settings
          live on your soccer or running profile pages.
        </p>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {loading ? (
          <p>Loading...</p>
        ) : !editing ? (
          <>
            <div className="profile-view">
              <div className="profile-view-header">
                {photoSrc ? (
                  <img src={photoSrc} alt="" className="profile-view-avatar" />
                ) : (
                  <div className="profile-view-avatar-placeholder">
                    {profile?.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                )}
                <div className="profile-view-title">
                  <h3>{profile?.name}</h3>
                  <p className="profile-view-status">{profile?.email}</p>
                </div>
              </div>
              <div className="profile-view-details">
                <p><strong>Phone:</strong> {profile?.phoneNumber || '—'}</p>
                <p><strong>Date of birth:</strong> {formatDate(profile?.dateOfBirth)}</p>
              </div>
            </div>
            <button type="button" className="btn btn-primary" onClick={() => setEditing(true)}>
              Edit account details
            </button>
          </>
        ) : (
          <form onSubmit={handleSave}>
            <div className="form-group">
              <label htmlFor="name">Name *</label>
              <input id="name" name="name" value={formData.name} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input value={profile?.email || ''} disabled />
            </div>
            <div className="form-group">
              <label htmlFor="phoneNumber">Phone number</label>
              <input id="phoneNumber" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label htmlFor="dateOfBirth">Date of birth</label>
              <input type="date" id="dateOfBirth" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Profile picture</label>
              <input type="file" accept="image/*" onChange={handleProfilePictureChange} />
              {photoSrc && (
                <div className="profile-picture-preview">
                  <img src={photoSrc} alt="" />
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setEditing(false)}>
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      <div className="card account-profile-page__card">
        <h2>Sport profiles</h2>
        <ul className="account-profile-page__sport-links">
          <li>
            <Link to={soccerPath('/players/profile')}>Soccer player profile</Link>
            {profile?.hasSoccerProfile ? ' — set up' : ' — not set up yet'}
          </li>
          <li>
            <Link to={runningPath('/profile')}>Running profile</Link>
            {profile?.hasRunningProfile ? ' — set up' : ' — not set up yet'}
          </li>
        </ul>
      </div>

      <div className="card account-profile-page__card">
        <h2>Change password</h2>
        <form onSubmit={handlePasswordSubmit}>
          <div className="form-group">
            <label htmlFor="currentPassword">Current password</label>
            <input
              type="password"
              id="currentPassword"
              name="currentPassword"
              value={passwordData.currentPassword}
              onChange={handlePasswordChange}
              autoComplete="current-password"
            />
          </div>
          <div className="form-group">
            <label htmlFor="newPassword">New password</label>
            <input
              type="password"
              id="newPassword"
              name="newPassword"
              value={passwordData.newPassword}
              onChange={handlePasswordChange}
              autoComplete="new-password"
            />
          </div>
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm new password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={passwordData.confirmPassword}
              onChange={handlePasswordChange}
              autoComplete="new-password"
            />
          </div>
          <button type="submit" className="btn btn-secondary" disabled={saving}>
            Update password
          </button>
        </form>
      </div>
    </div>
  );
};

export default AccountProfile;
